/**
 * Morpho-type vaults that are NOT products, refused by address on every read.
 *
 * The MetaMorpho / Vaults V2 factory deployment script deploys a 129-byte
 * `DummyERC20` (approve() + decimals() only; everything else reverts) and one
 * nameless, empty vault over it in the same tx, on every chain. Discovered from
 * the factory's create events or from the Feather index it looks like any
 * other vault, and 17 of them reached the `morphoTypeVaults` registry
 * (lender-metadata `MORPHO_STUB_VAULTS.md` / `data/morpho-stub-vaults.json`).
 *
 * The registry is purged and its append jobs guarded, but the registry is a
 * PUBLISHED file this fetcher has no say over — so the vault is refused here
 * too, by address, and a row deleted from the DB stays deleted through a stale
 * bundle. Keyed on the VAULT, deliberately: the dummy token is never referenced
 * by anything else, so there is nothing to gain from blacklisting it, and a
 * token-level rule is one more thing to get wrong.
 */
const MORPHO_STUB_VAULTS: Record<string, readonly string[]> = {
  '14': ['0x5eae7e544258e421cb2774e508e15ee8dade8200'], // Flare
  '146': ['0xd7f2f89e1991de2b9d62fbeeeb3bc0b01d032328'], // Sonic
  '239': ['0x2903a9e55bb8a05e6cbdd5c5d00203bf527fa9db'], // TAC
  '1135': ['0x389724731cea95c4a46cc93e96f211f389f31405'], // Lisk
  '1672': ['0x79ded579756072372510ff3a86f884433a697b5c'], // Pharos
  '1868': ['0x94665e0df3c8c25119d80b2e3c703ccd127bf37e'], // Soneium
  '2741': ['0x225c6e63970bb04d0780b3abb047dba659ad3cec'], // Abstract
  '2818': ['0x7cf2c1a184c2f17e0413a13b21b1fdafd51df08c'], // Morph
  '4114': ['0xc063aca30b0d56ff0a9e446a94f8cdb421ab89fb'], // Citrea
  '4326': ['0xf55c695ebaf1f4eefeda0833ef6a29c90e7b7f05'], // MegaETH
  '34443': ['0xf4461806c58d9e7cd74e79b113d81d127634b807'], // Mode
  '42220': ['0x099272b39ae8e6d7d415e8ba252c3d2c59432087'], // Celo
  '43111': ['0x339b3b6413345b4d3beb524c12cd3910c6dd8dbb'], // Hemi
  '57073': ['0xb00123b1058c13559408b9d609ed417617f588ed'], // Ink
  '59144': ['0x3e89134a270b8f0dc3dfd8bf6e249fddb2f7a634'], // Linea
  '98866': [
    '0x6f8acb9c03abb7ba6ba24b4698b019ffe96eae60',
    '0xd2586890224ab02bf31334e15872cd9c93f68ba8',
  ], // Plume
}

const STUB_SET = new Map(
  Object.entries(MORPHO_STUB_VAULTS).map(([chainId, list]) => [
    chainId,
    new Set(list.map((a) => a.toLowerCase())),
  ]),
)

/** True when `vault` on `chainId` is a known factory smoke-test vault. */
export const isMorphoStubVault = (chainId: string, vault: string): boolean =>
  STUB_SET.get(chainId)?.has(vault.toLowerCase()) ?? false
